import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Alert,
  CircularProgress,
} from '@mui/material';
import systemModuleService, { SystemModule } from '../../services/system-module.service';
import userService from '../../services/user.service';

const UserModuleAccess: React.FC = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const [module, setModule] = useState<SystemModule | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [userAccess, setUserAccess] = useState<Map<number, boolean>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [moduleId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch module details
      const moduleResponse = await systemModuleService.getAllModules();
      const foundModule = moduleResponse.data?.find((m: SystemModule) => m.id === parseInt(moduleId || '0'));
      setModule(foundModule || null);

      // Fetch all users
      const usersResponse = await userService.getUserProfiles({});
      setUsers(usersResponse.data || []);

      // Fetch access for all users
      const accessMap = new Map<number, boolean>();
      for (const user of usersResponse.data || []) {
        try {
          const accessResponse = await systemModuleService.getUserModuleAccess(user.id);
          const access = accessResponse.data?.find((a: any) => a.systemModule?.id === parseInt(moduleId || '0'));
          accessMap.set(user.id, access?.hasAccess ?? true); // Default to true if not set
        } catch (err) {
          accessMap.set(user.id, true); // Default to true on error
        }
      }
      setUserAccess(accessMap);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAccess = async (userId: number, currentAccess: boolean) => {
    try {
      await systemModuleService.setUserModuleAccess(userId, parseInt(moduleId || '0'), !currentAccess);
      setUserAccess(new Map(userAccess.set(userId, !currentAccess)));
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update access');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        User Access for: {module?.name || 'Unknown Module'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="center">Has Access</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.role?.name || 'N/A'}</TableCell>
                <TableCell align="center">
                  <Switch
                    checked={userAccess.get(user.id) ?? true}
                    onChange={() => handleToggleAccess(user.id, userAccess.get(user.id) ?? true)}
                    color="primary"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UserModuleAccess;

